/**
 * Phase 6 cross-cutting PR 2 — `<NotifRow>`.
 *
 * Single notification row inside the dropdown. Renders emoji glyph
 * (tone-coloured per type per direction.md §1) + title + message +
 * time-ago. Unread rows wear a 3px `--signal` left-rule + soft tint
 * (matches the 6b2 RosterRow left-rule encoding).
 *
 * Click → calls `onActivate(id)` which marks the row read and
 * triggers navigate (or modal open in the case of `ai_escalation`
 * — PR 2b will add that branch; for now ai_escalation falls back to
 * /v2/chat per the secondary CTA).
 */

import { useNavigate } from 'react-router-dom';
import {
  NOTIFICATION_ICON,
  NOTIFICATION_META,
  getNotificationClickPath,
  notificationTimeAgo,
  sanitizeInternalHref,
} from '../../lib/notifAllowlist';
import { type Notification } from '../../hooks/useNotifications';
import { Icon } from '../icons/registry';
import { useIconSystem } from '../../hooks/useIconSystem';
import styles from './NotifRow.module.css';

interface NotifRowProps {
  notification: Notification;
  onActivate: (id: string) => void;
  /**
   * Phase 6 cross-cutting PR 2b — called when an `ai_escalation` row
   * is clicked, instead of navigating. The parent (BellTrigger) opens
   * the in-place `<EscalationSummaryModal>` so the user stays on
   * their caller page. For all other notification types this prop
   * is ignored.
   */
  onEscalationClick?: (notification: Notification) => void;
}

export function NotifRow({ notification, onActivate, onEscalationClick }: NotifRowProps): JSX.Element {
  const navigate = useNavigate();
  const meta = NOTIFICATION_META[notification.type];
  const icon = NOTIFICATION_ICON[notification.type];
  const iconsOn = useIconSystem();
  const destination = getNotificationClickPath(notification.type);

  const handleClick = (): void => {
    onActivate(notification.id);
    // ai_escalation has no route destination — getNotificationClickPath
    // returns null, signalling that the parent should open the
    // in-place EscalationSummaryModal instead.
    if (notification.type === 'ai_escalation' && onEscalationClick) {
      onEscalationClick(notification);
      return;
    }
    // NU-1 — plan_nudge rows may carry a deep link in metadata.href
    // (untrusted, server-stored). sanitizeInternalHref is the only
    // guard; anything it rejects falls back to the hardcoded /plan.
    if (notification.type === 'plan_nudge') {
      navigate(sanitizeInternalHref(notification.metadata?.href) ?? destination ?? '/plan');
      return;
    }
    // F-514 — relationship_invite deep-links per DIRECTION via the server-set
    // metadata.href (invited adult → /guardian; invited athlete →
    // /settings#guardians, also the metadata-less fallback for pre-F-514
    // rows). Same sanitize-or-fallback contract as plan_nudge.
    if (notification.type === 'relationship_invite') {
      navigate(
        sanitizeInternalHref(notification.metadata?.href) ?? destination ?? '/settings#guardians',
      );
      return;
    }
    if (destination) navigate(destination);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLButtonElement>): void => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      handleClick();
    }
  };

  return (
    <button
      type="button"
      className={[styles.row, notification.is_read ? styles.read : styles.unread]
        .filter(Boolean)
        .join(' ')}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      aria-label={`${notification.title}${notification.is_read ? '' : ' (unread)'}`}
    >
      {/* CD-ICONS-01 — the row glyph, @20 (README: lists).
          ⚠️ The tone CLASS is untouched and keeps doing the colouring, which is
          exactly the currentColor contract: `--danger` on an escalation row is
          the SURFACE's, and `sys.warn` inherits it rather than carrying one.
          Five of nine types still have no ratified glyph and keep their emoji
          in both flag states — see NOTIFICATION_META. */}
      <span className={[styles.glyph, styles[`tone-${meta.tone}`]].join(' ')} aria-hidden="true">
        {iconsOn && icon ? <Icon name={icon} size={20} /> : meta.emoji}
      </span>
      <span className={styles.body}>
        <span className={styles.title}>{notification.title}</span>
        {notification.message ? (
          <span className={styles.message}>{notification.message}</span>
        ) : null}
        <span className={styles.time}>{notificationTimeAgo(notification.created_at)}</span>
      </span>
    </button>
  );
}
